// src/components/ImageProcessor.js
import React, { useState, useRef, useEffect } from 'react';

// Shared color list
const COLOR_NAMES = [
  'Pink',
  'Beige',
  'Red',
  'Deep Red',
  'Brown',
  'Light Brown',
  'Yellow',
  'Orange',
  'Olive Green',
  'Green',
  'Pine Green',
  'Turquoise',
  'Blue',
  'Deep Blue',
  'Light Blue',
  'Purple',
  'White',
  'Black',
  'Grey',
];

 export default function ImageProcessor({
   filename,
   setFilename,
   isProcessed,
   setIsProcessed,
   email,
   setErrorState,
   errorState,
   credits,
   processedImage,
   setProcessedImage,
   overlayImage,
   setOverlayImage,
   setColorListExternal,
   resetTrigger,
   onResetComplete,
   inputImageUrl,
   initialBaseColor,
   initialColorList,
   initAdvancedPanelKey,
   onStartNewProject,
   capitanApi,
   hydrateGate = true,
   projectRoot,
   initialRemoveBkgrnd = false,
   initialCloseBkrndHoles = false,
   initialRemoveIslands = false,
   initialSpeckClean = false,
   initialMkFreestanding = false,
   initialBckgrndInflate = 40,
 }) {
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);

  const [inputImage, setInputImage] = useState(null);
  const [numColors, setNumColors] = useState(4);
  const [pendingNumColors, setPendingNumColors] = useState(4);
  const [backgroundInflate, setBackgroundInflate] = useState(50);
  const [inflationValues, setInflationValues] = useState([]);

  const [colorList, setColorList] = useState([]);
  const [dropdownDefaultsVisible, setDropdownDefaultsVisible] = useState(true);
  const [baseColor, setBaseColor] = useState('');
  const [processDisabled, setProcessDisabled] = useState(true);

  const [speckleFlags, setSpeckleFlags] = useState([]);
  const [removeNoisyBackground, setRemoveNoisyBackground] = useState(false);
  const [closeBackgroundHoles, setCloseBackgroundHoles] = useState(true);
  const [thresholdValues, setThresholdValues] = useState([]);
  const [showColorControls, setShowColorControls] = useState(false);
  const [removeSmallIslands, setRemoveSmallIslands] = useState(false);
  const [speckleAutoCleanup, setSpeckleAutoCleanup] = useState(true);

  const [isProcessing, setIsProcessing] = useState(false);
  const [processedLoaded, setProcessedLoaded] = useState(false);
  const [overlayLoaded, setOverlayLoaded] = useState(false);

  const [showInputOverlayPanel, setShowInputOverlayPanel] = useState(false);
  const [showAdvancedOverlayPanel, setShowAdvancedOverlayPanel] = useState(false);

  const orangeBoxRef = useRef(null);

  const [baseNaturalSize, setBaseNaturalSize] = useState({ w: 0, h: 0 });
  const [kcHover, setKcHover] = useState(false);
  
  // --- One-shot auto-open flags (no auto-close) ------------------------------
  const autoOpenBasicOnceRef = useRef(false);
  const autoOpenAdvancedOnceRef = useRef(false);

  // ─────────────────────────────────────────────
  // KEYCHAIN OVERLAY STATE
  // ─────────────────────────────────────────────
  const kcCanvasRef = useRef(null);
  const kcContainerRef = useRef(null);

  // Keychain loop is always enabled for KC mode
  const [kcEnabled, setKcEnabled] = useState(false);
  const [waitingForKCClick, setWaitingForKCClick] = useState(false);
  const [showKCOverlay, setShowKCOverlay] = useState(true);
  // normalized center (0–1) within the box
  const [kcCenter, setKcCenter] = useState({ x: 0.5, y: 0.12 });
  const [kcInnerDia, setKcInnerDia] = useState(15);  // mm
  const [kcOuterDia, setKcOuterDia] = useState(25);  // mm
  const [kcDragging, setKcDragging] = useState(false);
  const [kcIsPunching, setKcIsPunching] = useState(false);


  // --- URL helpers ------------------------------------------------------------
  const toPreviewPrefix = (url) => {
    if (!url) return '';
    const s = String(url).trim();
    if (s.startsWith('/preview/')) return s;
    if (s.startsWith('/outputs/')) return '/preview/' + s.slice('/outputs/'.length);
    return s;
  };

  const normalizeUrl = (p) => {
    if (!p) return '';
    let url = String(p).trim();

    if (/^(https?:)?\/\//i.test(url) || /^data:/i.test(url) || url.startsWith('blob:')) {
      try {
        const u = new URL(url, window.location.origin);
        u.pathname = toPreviewPrefix(u.pathname);
        return u.toString();
      } catch {
        return toPreviewPrefix(url);
      }
    }

    if (!url.startsWith('/')) url = '/' + url;
    url = toPreviewPrefix(url);
    return url;
  };

  const extractProjectRoot = (urlLike) => {
    if (!urlLike) return '';
    const s = String(urlLike);
    the: {
      const m = s.match(/(\/(?:outputs|preview)\/[^/]+\/output-[^/]+)/i);
      return m ? m[1] : '';
    }
  };

  const buildRootFromEmailFilename = (emailArg, filenameArg) => {
    const username = (emailArg || '').split('@')[0];
    const baseNoExt = String(filenameArg || '').split('.').slice(0, -1).join('');
    if (!username || !baseNoExt) return '';
    return `/preview/${username}/output-${baseNoExt}`;
  };

  const userIntentRef = useRef('idle'); // 'idle' | 'upload' | 'open'

  const paddedInflate = [
    backgroundInflate,
    ...Array(pendingNumColors - 1)
      .fill(0)
      .map((_, i) => Number(inflationValues[i + 1] ?? 0)),
  ];
  const paddedSpeckles = [...Array(pendingNumColors)].map((_, i) =>
    Number(speckleFlags[i] ?? 0)
  );
  const paddedThresholds = [...Array(pendingNumColors)].map((_, i) =>
    Number(thresholdValues[i] ?? 0)
  );

  const colorUpdateTimer = useRef(null);
  const lastSentColorsRef = useRef(null);

  // Relaxed guard: just require we know which project/user this is
  const canSendColorUpdates = () =>
    !!filename && !!email && !isProcessing;

  const scheduleColorUpdate = (nextSelectedColors) => {
    if (
      !Array.isArray(nextSelectedColors) ||
      nextSelectedColors.length === 0 ||
      !canSendColorUpdates()
    ) {
      return;
    }

    const key = JSON.stringify(nextSelectedColors);
    if (lastSentColorsRef.current === key) return;

    if (colorUpdateTimer.current) {
      clearTimeout(colorUpdateTimer.current);
    }

    colorUpdateTimer.current = setTimeout(() => {
      lastSentColorsRef.current = key;
      triggerColorUpdate(nextSelectedColors);
    }, 350);
  };

  const triggerColorUpdate = async (selectedColors) => {
    if (
      !filename ||
      !email ||
      !Array.isArray(selectedColors) ||
      selectedColors.length === 0
    ) {
      return;
    }

    setIsProcessing(true);
    try {
      const res = await fetch('/update-colors/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename,
          email,
          numColors: selectedColors.length,
          selectedColors,
        }),
      });

      if (!res.ok) {
        console.error('update-colors failed:', await res.text());
        setErrorState(true);
        return;
      }

      const data = await res.json().catch(() => ({}));
      setProcessedLoaded(false);
      setOverlayLoaded(false);
      updatePreviews(data);
      setErrorState(false);
    } catch (e) {
      console.error('update-colors error:', e);
      setErrorState(true);
    } finally {
      // ensure we always release the spinner for this route
      setIsProcessing(false);
    }
  };


  // ----------------- HARD RESET before new upload -----------------
  function resetAllLocalStateForNewUpload() {
    if (colorUpdateTimer.current) {
      clearTimeout(colorUpdateTimer.current);
      colorUpdateTimer.current = null;
    }

    lastSentColorsRef.current = null;

    setInputImage(null);
    setProcessedImage(null);
    setOverlayImage(null);
    setIsProcessed(false);
    setProcessDisabled(false);
    setErrorState(false);

    setBaseColor('');
    setColorList([]);
    setNumColors(4);
    setPendingNumColors(4);
    setDropdownDefaultsVisible(true);
    setShowColorControls(false);
    setSpeckleFlags([0, 0, 0, 0, 0, 0, 0, 0]);
    setThresholdValues(Array(8).fill(0));
    setInflationValues([]);
    setBackgroundInflate(50);
    setRemoveNoisyBackground(false);
    setCloseBackgroundHoles(true);
    setRemoveSmallIslands(false);
    setSpeckleAutoCleanup(true);
    setProgress(null);

    setIsProcessing(false);
    setProcessedLoaded(false);
    setOverlayLoaded(false);

    setShowInputOverlayPanel(false);
    setShowAdvancedOverlayPanel(false);

    // reset keychain overlay state
    setKcCenter({ x: 0.5, y: 0.12 });
    setKcInnerDia(15);
    setKcOuterDia(25);
    setKcDragging(false);
    setKcIsPunching(false);


    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  // ----------------- OPEN PROJECT hydrate -----------------
  useEffect(() => {
    if (!hydrateGate) return;
    if (userIntentRef.current === 'upload') return;

    userIntentRef.current = 'open';
    setShowColorControls(true);

    if (!inputImageUrl && !filename && !initAdvancedPanelKey) return;

    if (inputImageUrl) setInputImage(inputImageUrl);
    setProcessDisabled(false);
    setIsProcessed(true);

    const derivedRoot =
      projectRoot ||
      extractProjectRoot(inputImageUrl || '') ||
      buildRootFromEmailFilename(email, filename);

    if (!derivedRoot) {
      console.warn(
        '[ImageProcessor] Could not determine project root. Pass projectRoot from App.js.'
      );
    }

    const ts = Date.now();
    if (derivedRoot) {
      setProcessedLoaded(false);
      setOverlayLoaded(false);
      setOverlayImage(
        `${normalizeUrl(`${derivedRoot}/vectors/combined_colors.png`)}?t=${ts}`
      );
    }
    setDropdownDefaultsVisible(false);

    (async () => {
      if (!derivedRoot) return;
      const metaUrl = `${normalizeUrl(
        `${derivedRoot}/metadata.json`
      )}?ts=${Date.now()}`;
      try {
        const res = await fetch(metaUrl, { cache: 'no-store' });
        if (!res.ok) {
          console.warn(
            '[ImageProcessor] metadata fetch failed:',
            res.status,
            res.statusText
          );
          return;
        }
        const meta = await res.json();

        let baseRel = String(meta?.base_svg || '').replace(/^\.\//, '').trim();
        if (baseRel) {
          if (!baseRel.includes('/')) baseRel = `vectors/base/${baseRel}`;
          setProcessedLoaded(false);
          setProcessedImage(
            `${normalizeUrl(`${derivedRoot}/${baseRel}`)}?t=${Date.now()}`
          );
        } else {
          console.warn(
            '[ImageProcessor] metadata.base_svg missing; overlay will be visible alone.'
          );
        }

        const normPath = (p) =>
          String(p || '')
            .replace(/^\.\//, '')
            .replace(/\/+/g, '/')
            .trim()
            .toLowerCase();

        const pickColorName = (p) => {
          if (!p || typeof p !== 'string') return '';
          const mm = p.match(
            /(?:^|\/)\s*\d+\s*-\s*([^./]+)\.(?:svg|png)$/i
          );
          return mm && mm[1] ? mm[1].trim() : '';
        };

        const snap = (c) => {
          if (!c) return '';
          const found = COLOR_NAMES.find(
            (n) => n.toLowerCase() === String(c).toLowerCase()
          );
          return found || c;
        };

        const baseSvgRel = String(meta?.base_svg || '')
          .replace(/^\.\//, '')
          .trim();
        const baseSvgNorm = normPath(baseSvgRel);
        let base = pickColorName(baseSvgRel);

        let rest = Array.isArray(meta.colors)
          ? meta.colors
              .filter((e) => e && (e.file || e.name))
              .filter((e) => String(e.name).toLowerCase() !== 'base')
              .filter((e) => {
                const fileNorm = normPath(e.file);
                return !(fileNorm && baseSvgNorm && fileNorm === baseSvgNorm);
              })
              .map((e) =>
                e.file ? pickColorName(e.file) : String(e.name || '')
              )
              .filter(Boolean)
          : [];

        if (!base && Array.isArray(meta.colors)) {
          const baseObj = meta.colors.find(
            (c) => String(c?.name).toLowerCase() === 'base'
          );
          if (baseObj?.file) base = pickColorName(baseObj.file);
          if (!base && baseObj?.name) base = String(baseObj.name).trim();
        }

        const baseNorm = snap(base);
        const restNorm = rest.map(snap).filter(Boolean);
        const parsed = [baseNorm, ...restNorm].filter(Boolean);

        if (parsed.length >= 2) {
          const total = Math.min(8, Math.max(2, parsed.length));
          setBaseColor(parsed[0]);
          setColorList(parsed.slice(1));
          setNumColors(total);
          setPendingNumColors(total);
          setDropdownDefaultsVisible(false);
          setIsProcessed(true);
        }
      } catch (err) {
        console.error('[ImageProcessor] metadata load error:', err);
        requestAnimationFrame(() => setDropdownDefaultsVisible(false));
      }
    })();
  }, [inputImageUrl, initAdvancedPanelKey, filename, projectRoot]); // eslint-disable-line

  // ---------- Hydrate processing settings ----------
  useEffect(() => {
    if (!hydrateGate) return;
    if (userIntentRef.current === 'upload') return;

    const toBool = (v, fb = false) => {
      if (typeof v === 'boolean') return v;
      if (typeof v === 'number') return v !== 0;
      if (typeof v === 'string') {
        const t = v.trim().toLowerCase();
        if (['true', '1', 'yes', 'y', 'on'].includes(t)) return true;
        if (['false', '0', 'no', 'n', 'off'].includes(t)) return false;
      }
      return fb;
    };
    const clampInt = (n, lo, hi, fb) => {
      const v = parseInt(n, 10);
      if (!Number.isFinite(v)) return fb;
      return Math.max(lo, Math.min(hi, v));
    };

    setRemoveNoisyBackground(toBool(initialRemoveBkgrnd, false));
    setCloseBackgroundHoles(toBool(initialCloseBkrndHoles, false));
    setRemoveSmallIslands(toBool(initialRemoveIslands, false));
    setSpeckleAutoCleanup(toBool(initialSpeckClean, false));

    const safeInflate = clampInt(initialBckgrndInflate, 10, 100, 40);
    setBackgroundInflate(safeInflate);
  }, [
    hydrateGate,
    initAdvancedPanelKey,
    projectRoot,
    initialRemoveBkgrnd,
    initialCloseBkrndHoles,
    initialRemoveIslands,
    initialSpeckClean,
    initialBckgrndInflate,
  ]);

  // Reset on external reset
  useEffect(() => {
    if (resetTrigger) {
      setInputImage(null);
      setProcessedImage(null);
      setOverlayImage(null);
      setFilename('');
      setIsProcessed(false);
      setErrorState(false);
      setColorListExternal([]);
      setBaseColor('');
      setColorList([]);
      setPendingNumColors(4);
      setNumColors(4);
      setDropdownDefaultsVisible(true);
      setShowColorControls(false);
      setIsProcessing(false);
      setProcessedLoaded(false);
      setOverlayLoaded(false);
      setShowInputOverlayPanel(false);
      setShowAdvancedOverlayPanel(false);

      // reset keychain state on global reset
      setKcCenter({ x: 0.5, y: 0.12 });
      setKcInnerDia(15);
      setKcOuterDia(25);
      setKcDragging(false);
      setKcIsPunching(false);


      onResetComplete?.();
      if (fileInputRef.current) fileInputRef.current.value = '';
      userIntentRef.current = 'idle';
    }
  }, [
    resetTrigger,
    onResetComplete,
    setProcessedImage,
    setOverlayImage,
    setFilename,
    setIsProcessed,
    setErrorState,
    setColorListExternal,
  ]);

  // ---------- Robust preview updater ----------
  const updatePreviews = (data) => {
    const ts = Date.now();
    const d = data || {};

    const preview =
      d.preview_image ||
      d.previewImage ||
      d.processed_image ||
      d.processedImage ||
      d.base_svg ||
      d.baseSvg ||
      null;

    const overlay =
      d.overlay_image ||
      d.overlayImage ||
      d.combined_colors ||
      d.combinedColors ||
      d.flat_image ||
      d.flatImage ||
      null;

    if (preview) {
      setProcessedLoaded(false);
      setProcessedImage(`${normalizeUrl(preview)}?t=${ts}`);
    } else if (processedImage) {
      setProcessedLoaded(false);
      setProcessedImage(
        `${normalizeUrl(processedImage.split('?')[0])}?t=${ts}`
      );
    }

    if (overlay) {
      setOverlayLoaded(false);
      setOverlayImage(`${normalizeUrl(overlay)}?t=${ts}`);
    } else if (overlayImage) {
      setOverlayLoaded(false);
      setOverlayImage(
        `${normalizeUrl(overlayImage.split('?')[0])}?t=${ts}`
      );
    }
  };

  // sync internal -> external color list
  useEffect(() => {
    if (typeof setColorListExternal !== 'function') return;

    const hasAnyColor =
      (typeof baseColor === 'string' && baseColor.trim().length > 0) ||
      (Array.isArray(colorList) && colorList.length > 0);

    if (!hasAnyColor) return;

    const combined = baseColor
      ? [baseColor, ...(colorList || [])]
      : [...(colorList || [])];

    setColorListExternal(combined);
  }, [baseColor, colorList, setColorListExternal]);

  const handleInputClick = () => {
    capitanApi?.onUploadStart?.();
    userIntentRef.current = 'upload';
    resetAllLocalStateForNewUpload();
    onStartNewProject?.();
    fileInputRef.current?.click();
  };

  // Size arrays when color count changes
  useEffect(() => {
    setThresholdValues(Array(pendingNumColors).fill(0));
    setSpeckleFlags([0, ...Array(pendingNumColors - 1).fill(0)]);
  }, [pendingNumColors]);

  // Progress polling
  useEffect(() => {
    const username = (email || '').split('@')[0];
    if (!username) return;
    const interval = setInterval(() => {
      fetch(`/progress/${username}?ts=${Date.now()}`)
        .then((res) => {
          if (!res.ok) throw new Error('Progress not ready');
          return res.json();
        })
        .then((data) => {
          if (typeof data.progress === 'number') {
            setProgress(data.progress);
            setErrorState(false);
          }
        })
        .catch(() => setProgress(null));
    }, 1500);
    return () => clearInterval(interval);
  }, [email, setErrorState]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      alert('Only JPG, JPEG, or PNG files are allowed.');
      return;
    }

    const formData = new FormData();
    const username = (email || '').split('@')[0];
    formData.append('file', file);
    formData.append('username', username);

    // UI reset for new upload — keep Process disabled for now
    setInputImage(URL.createObjectURL(file));
    setIsProcessed(false);
    setDropdownDefaultsVisible(true);
    setBaseColor('');
    setColorList([]);
    setProcessDisabled(true);
    setProcessedImage(null);
    setOverlayImage(null);
    setIsProcessing(false);
    setProcessedLoaded(false);
    setOverlayLoaded(false);

    // reset keychain overlay state on new upload
    setKcCenter({ x: 0.5, y: 0.12 });
    setKcInnerDia(15);
    setKcOuterDia(25);
    setKcDragging(false);
    setKcIsPunching(false);

    try {
      const response = await fetch('/upload/', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setFilename(data.filename);
        setErrorState(false);

        setProcessDisabled(false);

      // Auto-open Basic Controls (one-shot, stays open)
      if (!autoOpenBasicOnceRef.current) {
        autoOpenBasicOnceRef.current = true;
        setShowInputOverlayPanel(true);
      }


      } else {
        console.error('Upload failed:', await response.text());
        setErrorState(true);
        setProcessDisabled(true);
      }
    } catch (error) {
      console.error('Error during upload:', error);
      setErrorState(true);
      setProcessDisabled(true);
    }

    orangeBoxRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleProcessClick = async (collectInputs = false) => {
    if (colorUpdateTimer.current) {
      clearTimeout(colorUpdateTimer.current);
      colorUpdateTimer.current = null;
    }
    lastSentColorsRef.current = null;

    if (!collectInputs) {
      setBaseColor('');
      setColorList([]);
      setDropdownDefaultsVisible(true);
      setShowColorControls(false);
    }

    let selectedColors = [];
    let inflateValuesLocal = [];
    if (collectInputs) {
      selectedColors = [
        (baseColor || '').trim(),
        ...Array.from({ length: pendingNumColors - 1 }, (_, i) =>
          (colorList[i] || '').trim()
        ),
      ].slice(0, pendingNumColors);
      inflateValuesLocal = [
        backgroundInflate,
        ...inflationValues.slice(1, pendingNumColors),
      ];
    }

    setNumColors(pendingNumColors);
    if (!collectInputs) setInflationValues(Array(pendingNumColors).fill(0));

    autoOpenAdvancedOnceRef.current = false; // allow auto-open for this new processing run

    setIsProcessing(true);
    setProcessDisabled(true);

    setProcessedImage(null);
    setOverlayImage(null);
    setProcessedLoaded(false);
    setOverlayLoaded(false);
    setIsProcessed(false);

    try {
      const response = await fetch('/process-kc/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename,
          email,
          numColors: pendingNumColors,
          backgroundInflate,
          inflateValues: collectInputs ? inflateValuesLocal : [],
          selectedColors: collectInputs ? selectedColors : [],
          speckleFlags: collectInputs ? speckleFlags : [],
          thresholdValues: Array.from(
            { length: pendingNumColors },
            (_, i) => thresholdValues[i] || 0
          ),
          removeNoisyBackground,
          closeBackgroundHoles,
          removeSmallIslands,
          speckleAutoCleanup,
        }),
      });

      if (response.ok) {
        const data = await response.json();

        if (
          data.adjusted_num_colors &&
          data.adjusted_num_colors !== pendingNumColors
        ) {
          setNumColors(data.adjusted_num_colors);
          setPendingNumColors(data.adjusted_num_colors);
        }

        updatePreviews(data);
        setDropdownDefaultsVisible(false);
        setErrorState(false);
        setIsProcessed(true);

        // --- UPDATED COLOR SYNC (process) ---
        if (Array.isArray(data.color_names) && data.color_names.length > 0) {
          const rawBase = String(data.color_names[0] || '').trim();
          const baseResolved =
            COLOR_NAMES.find(
              (c) => c.toLowerCase() === rawBase.toLowerCase()
            ) || rawBase;

          const userColors = data.color_names.slice(1).map((c) => {
            const name = String(c || '').trim();
            return (
              COLOR_NAMES.find(
                (n) => n.toLowerCase() === name.toLowerCase()
              ) || name
            );
          });

          setBaseColor(baseResolved);
          setColorList(userColors);

          if (typeof setColorListExternal === 'function') {
            setColorListExternal([baseResolved, ...userColors]);
          }
        } else {
          setBaseColor('');
          setColorList([]);
          if (typeof setColorListExternal === 'function') {
            setColorListExternal([]);
          }
        }
      } else {
        console.error('Processing failed:', await response.text());
        setErrorState(true);
      }
    } catch (error) {
      console.error('Error during processing:', error);
      setErrorState(true);
    } finally {
      setProcessDisabled(false);
    }

    orangeBoxRef.current?.scrollTo({
      top: orangeBoxRef.current.scrollHeight,
      behavior: 'smooth',
    });
  };

  // ---- Reusable Update Design ----
  const handleUpdateDesign = async (overrides = {}) => {
    const {
      num = numColors,
      base = baseColor,
      colors = colorList,
      background = backgroundInflate,
      inflate = paddedInflate,
      speckles = paddedSpeckles,
      thresholds = paddedThresholds,
      rnb = removeNoisyBackground,
      cbh = closeBackgroundHoles,
      rsi = removeSmallIslands,
      sac = speckleAutoCleanup,
    } = overrides;

    setIsProcessing(true);
    window.dispatchEvent(new CustomEvent('resetSTLDownloads'));

    setProcessedLoaded(false);
    setOverlayLoaded(false);

    try {
      const selectedColorsLocal = [
        (base || '').trim(),
        ...Array.from(
          { length: Math.max(0, (num || 0) - 1) },
          (_, i) => (colors[i] || '').trim()
        ),
      ].slice(0, num || 0);

      const response = await fetch('/update-design-kc/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename,
          email,
          numColors: num,
          backgroundInflate: background,
          inflateValues: inflate,
          selectedColors: selectedColorsLocal,
          speckleFlags: speckles,
          thresholdValues: thresholds,
          removeNoisyBackground: rnb,
          closeBackgroundHoles: cbh,
          removeSmallIslands: rsi,
          speckleAutoCleanup: sac,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        updatePreviews(data);
        setErrorState(false);

        // --- UPDATED COLOR SYNC (update-design) ---
        if (Array.isArray(data.color_names) && data.color_names.length > 0) {
          const rawBase = String(data.color_names[0] || '').trim();
          const baseResolved =
            COLOR_NAMES.find(
              (c) => c.toLowerCase() === rawBase.toLowerCase()
            ) || rawBase;

          const userColors = data.color_names.slice(1).map((c) => {
            const name = String(c || '').trim();
            return (
              COLOR_NAMES.find(
                (n) => n.toLowerCase() === name.toLowerCase()
              ) || name
            );
          });

          setBaseColor(baseResolved);
          setColorList(userColors);

          if (typeof setColorListExternal === 'function') {
            setColorListExternal([baseResolved, ...userColors]);
          }
        } else {
          setBaseColor('');
          setColorList([]);
          if (typeof setColorListExternal === 'function') {
            setColorListExternal([]);
          }
        }
      } else {
        console.error('Update Design failed:', await response.text());
        setErrorState(true);
        setIsProcessing(false);
      }
    } catch (err) {
      console.error('Error during update:', err);
      setErrorState(true);
      setIsProcessing(false);
    }
  };

  // ---- Initialize Advanced Color Panel from metadata ----
  useEffect(() => {
    if (!hydrateGate) return;
    if (userIntentRef.current === 'upload') return;
    if (baseColor || colorList.length > 0) return;

    const snapColor = (c) => {
      if (!c) return '';
      const found = COLOR_NAMES.find(
        (n) => n.toLowerCase() === String(c).toLowerCase()
      );
      return found || c;
    };

    const raw = [
      (initialBaseColor || '').trim(),
      ...((initialColorList || []).map((c) => (c || '').trim())),
    ].filter(Boolean);

    if (raw.length >= 2 && (processedImage || overlayImage)) {
      const snappedBase = snapColor(raw[0]);
      const snappedRest = raw.slice(1).map(snapColor);

      setBaseColor(snappedBase);
      setColorList(snappedRest);

      const total = Math.min(8, Math.max(2, 1 + snappedRest.length));
      setNumColors(total);
      setPendingNumColors(total);

      setDropdownDefaultsVisible(false);
      setShowColorControls(true);
      setIsProcessed(true);
      setProcessDisabled(false);
    }
  }, [
    initAdvancedPanelKey,
    processedImage,
    overlayImage,
    initialBaseColor,
    initialColorList,
    baseColor,
    colorList.length,
    hydrateGate,
  ]);

  // hydrate base SVG when overlay present but processed missing
  useEffect(() => {
    if (!isProcessed) return;
    if (!overlayImage || processedImage) return;
    if (!email || !filename) return;

    const username = (email || '').split('@')[0];
    const baseNoExt = String(filename || '')
      .split('.')
      .slice(0, -1)
      .join('');
    if (!username || !baseNoExt) return;

    const root = `/preview/${username}/output-${baseNoExt}`;
    const ts = Date.now();

    (async () => {
      try {
        const res = await fetch(
          `${normalizeUrl(`${root}/metadata.json`)}?ts=${ts}`,
          { cache: 'no-store' }
        );
        if (!res.ok) {
          console.warn(
            '[ImageProcessor] metadata.json missing in overlay-only hydrate:',
            res.status
          );
          return;
        }
        const meta = await res.json();
        let baseRel = String(meta?.base_svg || '').replace(/^\.\//, '').trim();
        if (!baseRel) return;

        if (!baseRel.includes('/')) {
          baseRel = `vectors/base/${baseRel}`;
        }

        setProcessedLoaded(false);
        setProcessedImage(
          `${normalizeUrl(`${root}/${baseRel}`)}?t=${Date.now()}`
        );
      } catch (err) {
        console.error(
          '[ImageProcessor] overlay-only hydrate failed:',
          err
        );
      }
    })();
  }, [isProcessed, overlayImage, processedImage, email, filename, setProcessedImage]);

  // stop spinner when both images ready
  useEffect(() => {
    const bothReady = processedLoaded && (overlayImage ? overlayLoaded : true);
    if (!bothReady) return;

    setIsProcessing(false);

    // Auto-open Advanced Controls after processing finishes (one-shot, stays open)
    if (!autoOpenAdvancedOnceRef.current) {
      autoOpenAdvancedOnceRef.current = true;
      setShowColorControls(true);
      setShowAdvancedOverlayPanel(true);
    }
  }, [processedLoaded, overlayLoaded, overlayImage]);


  // ─────────────────────────────────────────────
  // KEYCHAIN OVERLAY DRAWING + EVENTS
  // ─────────────────────────────────────────────
  useEffect(() => {
      const canvas = kcCanvasRef.current;
      const container = kcContainerRef.current;
      if (!canvas || !container) return;

      const ctx = canvas.getContext("2d");

      // If cursor is NOT hovering → clear overlay & exit
      if (!kcHover) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          return;
      }

      // Resize canvas to container
      const { width, height } = container.getBoundingClientRect();
      canvas.width = width;
      canvas.height = height;

      ctx.clearRect(0, 0, width, height);

      // Convert normalized kcCenter → pixel center
      const cx = kcCenter.x * width;
      const cy = kcCenter.y * height;

      // Cursor-follow overlay circle sizes (your updated formula)
      const outerRadius =
          Math.max(width, height) * 0.0028 * kcOuterDia;
      const innerRadius =
          outerRadius * (kcInnerDia / kcOuterDia);

      // Draw outer black ring
      ctx.beginPath();
      ctx.arc(cx, cy, outerRadius, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(68, 68, 68, 0.6)";
      ctx.lineWidth = 2;
      ctx.stroke();

      // Draw inner transparent cutout
      ctx.beginPath();
      ctx.arc(cx, cy, innerRadius, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(68, 68, 68, 0.6)";
      ctx.lineWidth = 2;
      ctx.stroke();

  }, [kcCenter, kcOuterDia, kcInnerDia, kcHover]);

const handleKcPointerDown = async (e) => {
  if (!kcEnabled) return;
  const canvas = kcCanvasRef.current;
  if (!canvas) return;

  const rect = canvas.getBoundingClientRect();
  const x = e.clientX - rect.left;
  const y = e.clientY - rect.top;
  const width = rect.width;
  const height = rect.height;

  // Normalized coords
  const nx = Math.min(1, Math.max(0, x / width));
  const ny = Math.min(1, Math.max(0, y / height));

  // First click after overlay hide → trigger scripts
  if (waitingForKCClick) {
    setWaitingForKCClick(false);
    await runKCActionAndPunch(nx, ny);
    return;
  }

  // Otherwise: standard cursor-follow loop move
  setKcCenter({ x: nx, y: ny });
};


  const handleKcPointerMove = (e) => {
    if (!kcEnabled) return;
    const canvas = kcCanvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const width = rect.width;
    const height = rect.height;

    const nx = Math.min(1, Math.max(0, x / width));
    const ny = Math.min(1, Math.max(0, y / height));

    setKcCenter({ x: nx, y: ny });
  };

const handleKcCanvasClick = (e) => {
  if (!kcEnabled) return;
  const container = kcContainerRef.current;
  if (!container) return;

  // Measure click relative to the IMAGE BOX
  const rect = container.getBoundingClientRect();
  const px = e.clientX - rect.left; // pixel X in container
  const py = e.clientY - rect.top;  // pixel Y in container
  const boxW = rect.width;
  const boxH = rect.height;

  // Normalized 0–1 within the container (for the cursor-following overlay)
  const nx = Math.min(1, Math.max(0, px / boxW));
  const ny = Math.min(1, Math.max(0, py / boxH));

  // Move the loop overlay to the clicked position
  setKcCenter({ x: nx, y: ny });

  // --- Map container click → image coordinates (objectFit: 'contain') ---
  const imgW = baseNaturalSize.w || boxW;
  const imgH = baseNaturalSize.h || boxH;

  const scale = Math.min(boxW / imgW, boxH / imgH);
  const renderedW = imgW * scale;
  const renderedH = imgH * scale;

  const offsetX = (boxW - renderedW) / 2;
  const offsetY = (boxH - renderedH) / 2;

  // coordinates inside the *image* area
  const imgX = (px - offsetX) / scale;
  const imgY = (py - offsetY) / scale;

  const clampedImgX = Math.max(0, Math.min(imgW, imgX));
  const clampedImgY = Math.max(0, Math.min(imgH, imgY));

  // Run keychain_action.py + keychain_punch.py using IMAGE-space coordinates
  runKCActionAndPunch(nx, ny, clampedImgX, clampedImgY, imgW, imgH);
};

  const handleKcPointerUp = () => {
    if (kcDragging) setKcDragging(false);
  };

  // ─────────────────────────────────────────────
  // KEYCHAIN PUNCH LOGIC
  // ─────────────────────────────────────────────
  const runKeychainPunch = async () => {
    if (!kcEnabled || !filename || !email) return;
    const username = (email || '').split('@')[0];
    if (!username) return;

    setKcIsPunching(true);
    setIsProcessing(true);

    try {
      // 1) Punch the loop into the combined PNG/SVG
      const punchRes = await fetch('/run-keychain-punch/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username,
          filename,
          centerX: kcCenter.x,
          centerY: kcCenter.y,
          innerDiameter: kcInnerDia,
          outerDiameter: kcOuterDia,
        }),
      });

      if (!punchRes.ok) {
        console.error('run-keychain-punch failed:', await punchRes.text());
        setErrorState(true);
        return;
      }

      const punchData = await punchRes.json().catch(() => ({}));
      updatePreviews(punchData);

      // 2) Optionally run a follow-up script (e.g., re-vectorize / regenerate KC assets)
      try {
        const scriptRes = await fetch('/run-keychain-script/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username,
            filename,
          }),
        });
        if (!scriptRes.ok) {
          console.warn('run-keychain-script returned non-OK:', await scriptRes.text());
        }
      } catch (err) {
        console.warn('run-keychain-script network error (non-fatal):', err);
      }

      setErrorState(false);
    } catch (err) {
      console.error('Keychain punch error:', err);
      setErrorState(true);
    } finally {
      setKcIsPunching(false);
      setIsProcessing(false);
    }
  };

  const handleKCOverlayClick = (e) => {
    e.stopPropagation();

    // Enable KC overlay behavior only — no /update-colors/ sync
    setKcEnabled(true);
    setShowKCOverlay(false);
    setWaitingForKCClick(true);
  };

const runKCActionAndPunch = async (nx, ny, px, py, boxW, boxH) => {
  const username = (email || '').split('@')[0];
  if (!username || !filename) return;

  try {
    setIsProcessing(true);
    setKcIsPunching(true);

    const headers = {
      'Content-Type': 'application/json',
      user: email || '',
      username,
      'X-Username': username,
    };

    // Clamp to safe positive ranges
    const clampedPx = Math.max(0, Math.min(boxW, px));
    const clampedPy = Math.max(0, Math.min(boxH, py));

    // Shared payload for both scripts
    const payload = {
      username,
      email,
      filename,

      // loop sizes (send ALL variants so both JS and Python callers are happy)
      inner: kcInnerDia,
      outer: kcOuterDia,
      innerDia: kcInnerDia,
      outerDia: kcOuterDia,
      innerDiameter: kcInnerDia,
      outerDiameter: kcOuterDia,
      // 🔑 these are the ones used by /run-keychain-script/ in main.py:
      inner_diameter: kcInnerDia,
      outer_diameter: kcOuterDia,

      // normalized center within the overlay box (0–1, always positive)
      centerX: -nx,
      centerY: -ny,

      // raw pixel coordinates inside the overlay box (used by Python punch script)
      position: [clampedPx, clampedPy],

      // Keep the extra debug info if backend ever needs it
      pixelX: clampedPx,
      pixelY: clampedPy,
      boxWidth: boxW,
      boxHeight: boxH,
    };


    // 1) keychain_action.py — builds loop.svg based on diameters + username/filename
    const res1 = await fetch('/run-keychain-script/', {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });

    if (!res1.ok) {
      console.error(
        'keychain_action (run-keychain-script) failed:',
        await res1.text()
      );
      setErrorState(true);
      return;
    }

    // 2) keychain_punch.py — uses the coordinates to punch the loop
    const res2 = await fetch('/run-keychain-punch/', {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });

    if (!res2.ok) {
      console.error('keychain_punch failed:', await res2.text());
      setErrorState(true);
      return;
    }

    const data = await res2.json().catch(() => ({}));
    updatePreviews(data);
    setErrorState(false);

    // ✅ After both scripts: hide loop, show KC overlay again
    setKcEnabled(false);
    setShowKCOverlay(true);
    setWaitingForKCClick(false);
  } catch (err) {
    console.error('KC action/punch error:', err);
    setErrorState(true);
  } finally {
    setKcIsPunching(false);
    setIsProcessing(false);
  }
};



  // ---- Helper: Advanced Color Controls ----
  const renderAdvancedControls = () => {
    if (!(processedImage && !dropdownDefaultsVisible && showColorControls)) {
      return null;
    }

    const activeCount = numColors || pendingNumColors;
    const indices = [...Array(activeCount).keys()];

    return (
      <>
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            marginTop: '1vh',
            fontSize: '1.5vh',
            padding: '0 0.1vw',
            boxSizing: 'border-box',
          }}
        >
          {/* Column headers */}
          <div
            style={{
              display: 'flex',
              width: '100%',
              marginBottom: '0.4vh',
              fontWeight: 'bold',
              textAlign: 'center',
              alignItems: 'center',
            }}
          >
            <div style={{ flexBasis: '40%', maxWidth: '40%' }}>Color</div>
            <div style={{ flexBasis: '18%', maxWidth: '18%' }}>Inflate</div>
            <div style={{ flexBasis: '20%', maxWidth: '20%' }}>Speckle Remove</div>
            <div style={{ flexBasis: '22%', maxWidth: '22%' }}>Threshold</div>
          </div>

          {/* Per-row controls */}
          {indices.map((i) => (
            <div
              key={`adv-row-${i}`}
              style={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                marginBottom: '0.2vh',
              }}
            >
              {/* COLOR column */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4vh',
                  flexBasis: '40%',
                  maxWidth: '40%',
                  paddingRight: '0.4vh',
                  boxSizing: 'border-box',
                }}
              >
                {/* [x] button */}
                <button
                  disabled={activeCount <= 2 || i === 0}
                  onClick={async () => {
                    if (activeCount <= 2 || i === 0) return;
                    const confirmed = window.confirm(
                      'Are you sure you want to delete this color?'
                    );
                    if (!confirmed) return;

                    const username = (email || '').split('@')[0];
                    const colorName = i === 0 ? baseColor : colorList[i - 1];
                    const pathSafe = (colorName || '')
                      .replace(/[^a-zA-Z0-9\s\-]/g, '')
                      .trim();

                    try {
                      const res = await fetch('/delete-png/', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          username,
                          filename,
                          colorIndex: i,
                          colorName: pathSafe,
                        }),
                      });
                      if (!res.ok) {
                        console.error('Delete PNG failed:', await res.text());
                        return;
                      }

                      const newColors = [...colorList];
                      const newInflate = [...inflationValues];
                      const newThresholds = [...thresholdValues];
                      const newSpeckles = [...speckleFlags];

                      if (i > 0) {
                        newColors.splice(i - 1, 1);
                      }
                      newInflate.splice(i, 1);
                      newThresholds.splice(i, 1);
                      newSpeckles.splice(i, 1);

                      const prevCount = activeCount;
                      const newNum = Math.max(2, prevCount - 1);

                      setColorList(newColors);
                      setInflationValues(newInflate);
                      setThresholdValues(newThresholds);
                      setSpeckleFlags(newSpeckles);
                      setPendingNumColors(newNum);
                      setNumColors(newNum);
                      setProcessDisabled(false);

                      const nextInflatePadded = [
                        backgroundInflate,
                        ...Array.from(
                          { length: Math.max(0, newNum - 1) },
                          (_, idx) => Number(newInflate[idx + 1] ?? 0)
                        ),
                      ];
                      const nextSpecklesPadded = Array.from(
                        { length: newNum },
                        (_, idx) => Number(newSpeckles[idx] ?? 0)
                      );
                      const nextThresholdsPadded = Array.from(
                        { length: newNum },
                        (_, idx) => Number(newThresholds[idx] ?? 0)
                      );

                      await handleUpdateDesign({
                        num: newNum,
                        base: baseColor,
                        colors: newColors,
                        background: backgroundInflate,
                        inflate: nextInflatePadded,
                        speckles: nextSpecklesPadded,
                        thresholds: nextThresholdsPadded,
                        rnb: removeNoisyBackground,
                        cbh: closeBackgroundHoles,
                        rsi: removeSmallIslands,
                        sac: speckleAutoCleanup,
                      });
                    } catch (err) {
                      console.error('Error deleting PNG:', err);
                    }
                  }}
                  style={{
                    color: 'red',
                    fontWeight: 'bold',
                    fontSize: '1.5vh',
                    border: 'none',
                    background: 'transparent',
                    cursor:
                      activeCount <= 2 || i === 0 ? 'not-allowed' : 'pointer',
                    flexShrink: 0,
                  }}
                >
                  ✕
                </button>

                {/* Color dropdown */}
                <select
                  className="adv-select"
                  style={{
                    padding: '0.4vh 0.8vh',
                    fontSize: '1.5vh',
                    flex: 1,
                    minWidth: 0,
                    background: 'rgba(255,255,255,0.12)',
                    border: '1px solid rgba(255,255,255,0.25)',
                    borderRadius: '0.6vh',
                    color: '#e5e7eb',
                    backdropFilter: 'blur(4px)',
                  }}
                  value={(() => {
                    const current =
                      (i === 0 ? baseColor : colorList[i - 1]) || '';
                    const match = COLOR_NAMES.find(
                      (n) => n.toLowerCase() === current.toLowerCase()
                    );
                    return match || current;
                  })()}
                  onChange={(e) => {
                    const newVal = e.target.value;

                    if (i === 0) {
                      if (newVal === baseColor) return;
                      const nextBase = newVal;
                      setBaseColor(nextBase);

                      const nextSelected = [
                        (nextBase || '').trim(),
                        ...Array.from(
                          { length: activeCount - 1 },
                          (_, idx) => (colorList[idx] || '').trim()
                        ),
                      ].slice(0, activeCount);
                      scheduleColorUpdate(nextSelected);
                    } else {
                      if (newVal === colorList[i - 1]) return;
                      const updatedColors = [...colorList];
                      updatedColors[i - 1] = newVal;
                      setColorList(updatedColors);

                      const nextSelected = [
                        (baseColor || '').trim(),
                        ...Array.from(
                          { length: activeCount - 1 },
                          (_, idx) => (updatedColors[idx] || '').trim()
                        ),
                      ].slice(0, activeCount);
                      scheduleColorUpdate(nextSelected);
                    }
                  }}
                >
                  {(() => {
                    const current =
                      (i === 0 ? baseColor : colorList[i - 1]) || '';
                    const match = COLOR_NAMES.find(
                      (n) => n.toLowerCase() === current.toLowerCase()
                    );
                    if (!dropdownDefaultsVisible && current && !match) {
                      return <option value={current}>{current}</option>;
                    }
                    return null;
                  })()}

                  {dropdownDefaultsVisible && (
                    <option value="">
                      {i === 0 ? 'Base' : `Color ${i + 1}`}
                    </option>
                  )}

                  {COLOR_NAMES.map((name, idx) => {
                    const isTakenElsewhere =
                      i > 0 &&
                      colorList.some(
                        (val, j) =>
                          j !== i - 1 &&
                          String(val || '')
                            .toLowerCase()
                            .trim() === name.toLowerCase()
                      );
                    return (
                      <option
                        key={idx}
                        value={name}
                        disabled={isTakenElsewhere}
                      >
                        {name}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* INFLATE column */}
              <div
                style={{
                  flexBasis: '18%',
                  maxWidth: '18%',
                  padding: '0 0.1vh',
                  boxSizing: 'border-box',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.25vh',
                    width: '100%',
                    maxWidth: '100%',
                  }}
                >
                  {/* – button */}
                  <button
                    type="button"
                    onClick={() => {
                      let val =
                        i === 0
                          ? backgroundInflate
                          : inflationValues[i] || 0;
                      val = val - 1;

                      if (i === 0) {
                        val = Math.max(10, val);
                        setBackgroundInflate(val);
                      } else {
                        val = Math.max(0, val);
                        const updated = [...inflationValues];
                        updated[i] = val;
                        setInflationValues(updated);
                      }
                    }}
                    style={{
                      width: '1.6vh',
                      height: '1.6vh',
                      borderRadius: '50%',
                      background: 'rgba(255,255,255,0.18)',
                      border: '1px solid rgba(255,255,255,0.25)',
                      color: 'white',
                      fontSize: '1.3vh',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      flexShrink: 0,
                    }}
                  >
                    –
                  </button>

                  {/* numeric input */}
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={
                      i === 0
                        ? backgroundInflate
                        : inflationValues[i] || 0
                    }
                    onChange={(e) => {
                      let raw = e.target.value.replace(/\D/g, '');
                      if (raw === '') raw = i === 0 ? '10' : '0';

                      let val = parseInt(raw, 10);
                      if (!Number.isFinite(val)) val = 0;

                      if (i === 0) {
                        val = Math.max(10, Math.min(100, val));
                        setBackgroundInflate(val);
                      } else {
                        val = Math.max(0, Math.min(10, val));
                        const updated = [...inflationValues];
                        updated[i] = val;
                        setInflationValues(updated);
                      }
                    }}
                    style={{
                      width: '4vh',
                      fontSize: '1.5vh',
                      padding: '0.25vh 0.4vh',
                      background: 'rgba(255,255,255,0.12)',
                      border: '1px solid rgba(255,255,255,0.25)',
                      borderRadius: '0.6vh',
                      color: '#e5e7eb',
                      textAlign: 'center',
                      outline: 'none',
                      backdropFilter: 'blur(4px)',
                      flexShrink: 0,
                      boxSizing: 'border-box',
                    }}
                  />

                  {/* + button */}
                  <button
                    type="button"
                    onClick={() => {
                      let val =
                        i === 0
                          ? backgroundInflate
                          : inflationValues[i] || 0;
                      val = val + 1;

                      if (i === 0) {
                        val = Math.min(100, val);
                        setBackgroundInflate(val);
                      } else {
                        val = Math.min(10, val);
                        const updated = [...inflationValues];
                        updated[i] = val;
                        setInflationValues(updated);
                      }
                    }}
                    style={{
                      width: '1.6vh',
                      height: '1.6vh',
                      borderRadius: '50%',
                      background: 'rgba(255,255,255,0.18)',
                      border: '1px solid rgba(255,255,255,0.25)',
                      color: 'white',
                      fontSize: '1.3vh',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      flexShrink: 0,
                    }}
                  >
                    +
                  </button>
                </div>
              </div>

              {/* SPECKLE column */}
              <div
                style={{
                  flexBasis: '20%',
                  maxWidth: '20%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: i === 0 ? 'not-allowed' : 'pointer',
                  }}
                >
                  <span
                    style={{
                      position: 'relative',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <input
                      type="checkbox"
                      disabled={i === 0}
                      checked={!!speckleFlags[i]}
                      onChange={(e) => {
                        if (i === 0) return;
                        const updated = [...speckleFlags];
                        updated[i] = e.target.checked ? 1 : 0;
                        setSpeckleFlags(updated);
                      }}
                      style={{
                        appearance: 'none',
                        WebkitAppearance: 'none',
                        MozAppearance: 'none',
                        width: '2.2vh',
                        height: '2.2vh',
                        borderRadius: '0.5vh',
                        border: '0.25vh solid rgba(255,255,255,0.8)',
                        backgroundColor: 'rgba(15,23,42,0.6)',
                        display: 'inline-block',
                        cursor: i === 0 ? 'not-allowed' : 'pointer',
                      }}
                    />
                    {!!speckleFlags[i] && (
                      <span
                        style={{
                          position: 'absolute',
                          fontSize: '1.7vh',
                          color: 'white',
                          pointerEvents: 'none',
                        }}
                      >
                        ✓
                      </span>
                    )}
                  </span>
                </label>
              </div>

              {/* THRESHOLD column */}
              <div
                style={{
                  flexBasis: '22%',
                  maxWidth: '22%',
                  padding: '0 0.1vh',
                  boxSizing: 'border-box',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.25vh',
                    width: '100%',
                    maxWidth: '100%',
                  }}
                >
                  {/* – button */}
                  <button
                    type="button"
                    disabled={!speckleFlags[i]}
                    onClick={() => {
                      if (!speckleFlags[i]) return;

                      let val = thresholdValues[i] || 0;
                      val = Math.max(0, val - 1);

                      const updated = [...thresholdValues];
                      updated[i] = val;
                      setThresholdValues(updated);
                    }}
                    style={{
                      width: '1.6vh',
                      height: '1.6vh',
                      borderRadius: '50%',
                      background: speckleFlags[i]
                        ? 'rgba(255,255,255,0.18)'
                        : 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.25)',
                      color: speckleFlags[i] ? 'white' : '#9ca3af',
                      fontSize: '1.3vh',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: speckleFlags[i] ? 'pointer' : 'not-allowed',
                      flexShrink: 0,
                    }}
                  >
                    –
                  </button>

                  {/* text input */}
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    disabled={!speckleFlags[i]}
                    value={thresholdValues[i] || 0}
                    onChange={(e) => {
                      if (!speckleFlags[i]) return;

                      let raw = e.target.value.replace(/\D/g, '');
                      if (raw === '') raw = '0';

                      let val = parseInt(raw, 10);
                      if (!Number.isFinite(val)) val = 0;

                      val = Math.max(0, Math.min(200, val));

                      const updated = [...thresholdValues];
                      updated[i] = val;
                      setThresholdValues(updated);
                    }}
                    style={{
                      width: '3.2vh',
                      padding: '0.25vh 0.3vh',
                      fontSize: '1.5vh',
                      background: speckleFlags[i]
                        ? 'rgba(255,255,255,0.12)'
                        : 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.25)',
                      borderRadius: '0.6vh',
                      color: speckleFlags[i] ? '#e5e7eb' : '#9ca3af',
                      textAlign: 'center',
                      outline: 'none',
                      backdropFilter: 'blur(4px)',
                      cursor: speckleFlags[i] ? 'text' : 'not-allowed',
                      flexShrink: 0,
                      boxSizing: 'border-box',
                    }}
                  />

                  {/* + button */}
                  <button
                    type="button"
                    disabled={!speckleFlags[i]}
                    onClick={() => {
                      if (!speckleFlags[i]) return;

                      let val = thresholdValues[i] || 0;
                      val = Math.min(200, val + 1);

                      const updated = [...thresholdValues];
                      updated[i] = val;
                      setThresholdValues(updated);
                    }}
                    style={{
                      width: '1.6vh',
                      height: '1.6vh',
                      borderRadius: '50%',
                      background: speckleFlags[i]
                        ? 'rgba(255,255,255,0.18)'
                        : 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.25)',
                      color: speckleFlags[i] ? 'white' : '#9ca3af',
                      fontSize: '1.3vh',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: speckleFlags[i] ? 'pointer' : 'not-allowed',
                      flexShrink: 0,
                    }}
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* KEYCHAIN LOOP CONTROLS – in Advanced panel */}
        <div
          style={{
            marginTop: '1vh',
            paddingTop: '0.8vh',
            borderTop: '1px solid rgba(148,163,184,0.7)',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.6vh',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span
              style={{
                fontSize: '2.1vh',
                fontWeight: 600,
                color: '#e5e7eb',
              }}
            >
              Keychain Loop
            </span>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '1vh',
              marginTop: '0.4vh',
            }}
          >
            {/* Inner Ø controls */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.6vh',
                flex: 1,
              }}
            >
              <span style={{ whiteSpace: 'nowrap' }}>Inner Ø (mm):</span>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.25vh',
                  width: '100%',
                  maxWidth: '100%',
                }}
              >
                {/* – button */}
                <button
                  type="button"
                  onClick={() => {
                    let v = kcInnerDia - 1;
                    if (!Number.isFinite(v)) v = 1;
                    v = Math.max(1, Math.min(20, v));
                    setKcInnerDia(v);
                  }}
                  style={{
                    width: '1.8vh',
                    height: '1.8vh',
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.18)',
                    border: '1px solid rgba(255,255,255,0.25)',
                    color: 'white',
                    fontSize: '1.4vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                >
                  –
                </button>

                {/* numeric input */}
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={kcInnerDia}
                  onChange={(e) => {
                    let raw = e.target.value.replace(/\D/g, '');
                    if (raw === '') raw = '1';
                    let v = parseInt(raw, 10);
                    if (!Number.isFinite(v)) v = 1;
                    v = Math.max(1, Math.min(20, v));
                    setKcInnerDia(v);
                  }}
                  style={{
                    width: '4vh',
                    fontSize: '1.8vh',
                    padding: '0.2vh 0.4vh',
                    background: 'rgba(255,255,255,0.12)',
                    border: '1px solid rgba(255,255,255,0.25)',
                    borderRadius: '0.6vh',
                    color: '#e5e7eb',
                    textAlign: 'center',
                    outline: 'none',
                    backdropFilter: 'blur(4px)',
                    flexShrink: 0,
                    boxSizing: 'border-box',
                  }}
                />

                {/* + button */}
                <button
                  type="button"
                  onClick={() => {
                    let v = kcInnerDia + 1;
                    if (!Number.isFinite(v)) v = 1;
                    v = Math.max(1, Math.min(20, v));
                    setKcInnerDia(v);
                  }}
                  style={{
                    width: '1.8vh',
                    height: '1.8vh',
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.18)',
                    border: '1px solid rgba(255,255,255,0.25)',
                    color: 'white',
                    fontSize: '1.4vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                >
                  +
                </button>
              </div>
            </div>

            {/* Outer Ø controls */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.6vh',
                flex: 1,
              }}
            >
              <span style={{ whiteSpace: 'nowrap' }}>Outer Ø (mm):</span>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.25vh',
                  width: '100%',
                  maxWidth: '100%',
                }}
              >
                {/* – button */}
                <button
                  type="button"
                  onClick={() => {
                    let v = kcOuterDia - 1;
                    if (!Number.isFinite(v)) v = kcInnerDia + 1;
                    v = Math.max(kcInnerDia + 1, Math.min(40, v));
                    setKcOuterDia(v);
                  }}
                  style={{
                    width: '1.8vh',
                    height: '1.8vh',
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.18)',
                    border: '1px solid rgba(255,255,255,0.25)',
                    color: 'white',
                    fontSize: '1.4vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                >
                  –
                </button>

                {/* numeric input */}
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={kcOuterDia}
                  onChange={(e) => {
                    let raw = e.target.value.replace(/\D/g, '');
                    if (raw === '') raw = String(kcInnerDia + 1);
                    let v = parseInt(raw, 10);
                    if (!Number.isFinite(v)) v = kcInnerDia + 1;
                    v = Math.max(kcInnerDia + 1, Math.min(40, v));
                    setKcOuterDia(v);
                  }}
                  style={{
                    width: '4vh',
                    fontSize: '1.8vh',
                    padding: '0.2vh 0.4vh',
                    background: 'rgba(255,255,255,0.12)',
                    border: '1px solid rgba(255,255,255,0.25)',
                    borderRadius: '0.6vh',
                    color: '#e5e7eb',
                    textAlign: 'center',
                    outline: 'none',
                    backdropFilter: 'blur(4px)',
                    flexShrink: 0,
                    boxSizing: 'border-box',
                  }}
                />

                {/* + button */}
                <button
                  type="button"
                  onClick={() => {
                    let v = kcOuterDia + 1;
                    if (!Number.isFinite(v)) v = kcInnerDia + 1;
                    v = Math.max(kcInnerDia + 1, Math.min(40, v));
                    setKcOuterDia(v);
                  }}
                  style={{
                    width: '1.8vh',
                    height: '1.8vh',
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.18)',
                    border: '1px solid rgba(255,255,255,0.25)',
                    color: 'white',
                    fontSize: '1.4vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                >
                  +
                </button>
              </div>
            </div>
          </div>
        </div>


        {/* Centered Update button */}
        <div
          style={{
            marginTop: '0.8vh',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <button
            onClick={() => handleUpdateDesign()}
            style={{
              padding: '0.4vh 2.2vh',
              fontSize: '1.9vh',
              borderRadius: '8px',
              border: '1px solid #94a3b8',
              fontWeight: 'bold',
              backgroundColor: '#e2e8f0',
              cursor: 'pointer',
            }}
          >
            Update Design
          </button>
        </div>
      </>
    );

  };

// ─────────── RENDER ───────────
// ✅ Disable ALL controls while rendering.gif is visible (isProcessing === true)
const uiDisabled = Boolean(isProcessing);

return (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      height: '100%',
      width: '100%',
      overflow: 'hidden',
      paddingTop: '0vh',
    }}
  >
    <input
      type="file"
      accept="image/jpeg,image/png"
      ref={fileInputRef}
      onChange={uiDisabled ? undefined : handleFileUpload}
      disabled={uiDisabled}
      style={{ display: 'none' }}
    />

    {/* FLEX ROW: Orange (images) */}
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'flex-start',
        gap: '0vw',
        height: 'auto',
      }}
    >
      {/* ORANGE BOX */}
      <div
        ref={orangeBoxRef}
        style={{
          border: '0px solid orange',
          padding: '0.5vw',
          width: '81vmin',
          height: '81vmin',
          maxWidth: '100%',
          maxHeight: '100%',
          overflowY: 'auto',
          boxSizing: 'border-box',
          display: 'block',
          margin: '0 auto',
        }}
      >
        {/* Input Image */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
            marginBottom: '2vh',
          }}
        >
          <div
            style={{
              ...boxStyle(),
              position: 'relative',
              cursor: credits > 0 && !uiDisabled ? 'pointer' : 'not-allowed',
              opacity: credits > 0 && !uiDisabled ? 1 : 0.6,
            }}
            onClick={credits > 0 && !uiDisabled ? handleInputClick : undefined}
          >
            {inputImage && (
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: 0,
                  zIndex: 30,
                  background:
                    'linear-gradient(to bottom, rgba(15,23,42,0.96), rgba(15,23,42,0.6), transparent)',
                  color: 'white',
                  transform: showInputOverlayPanel
                    ? 'translateY(0%)'
                    : 'translateY(calc(-100% + 2.8vh))',
                  transition: 'transform 0.25s ease-in-out',
                  willChange: 'transform',
                  fontSize: '0.8rem',
                  pointerEvents: uiDisabled ? 'none' : 'auto',
                  opacity: uiDisabled ? 0.6 : 1,
                }}
              >
                {/* Handle */}
                <div
                  onClick={
                    uiDisabled
                      ? undefined
                      : (e) => {
                          e.stopPropagation();
                          setShowInputOverlayPanel((prev) => !prev);
                        }
                  }
                  style={{
                    height: '2.8vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: uiDisabled ? 'not-allowed' : 'pointer',
                    borderBottom: '1px solid rgba(255,255,255,0.4)',
                    backgroundColor: 'rgba(112,112,112,0.4)',
                  }}
                >
                  <span
                    style={{
                      fontSize: '2.5vh',
                      fontWeight: 600,
                      letterSpacing: '0.03em',
                    }}
                  >
                    {showInputOverlayPanel
                      ? '▲ Basic Controls ▲'
                      : '▼ Basic Controls ▼'}
                  </span>
                </div>

                {/* CONTENT: Basic Controls */}
                <div
                  style={{
                    padding: showInputOverlayPanel
                      ? '8px 10px 10px'
                      : '0 10px 0',
                    maxHeight: showInputOverlayPanel ? '55%' : 0,
                    overflowY: 'auto',
                    overflow: 'hidden',
                    backgroundColor: 'rgba(112,112,112,0.5)',
                    fontSize: '2vh',
                    transition:
                      'max-height 0.25s ease, opacity 0.25s ease, padding 0.25s ease',
                    opacity: showInputOverlayPanel ? 1 : 0,
                  }}
                >
                  {/* Number of Colors */}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                      marginBottom: '8px',
                      gap: '1vh',
                      opacity: uiDisabled ? 0.6 : 1,
                      pointerEvents: uiDisabled ? 'none' : 'auto',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '2.5vh',
                        fontWeight: 600,
                        color: '#e5e7eb',
                        letterSpacing: '0.5px',
                        marginBottom: '0.5vh',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Number of Colors:
                    </span>

                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1vh',
                      }}
                    >
                      {/* – button */}
                      <button
                        type="button"
                        disabled={uiDisabled}
                        onClick={
                          uiDisabled
                            ? undefined
                            : () => {
                                let value = Math.max(2, pendingNumColors - 1);
                                setPendingNumColors(value);
                                setProcessDisabled(false);
                                setDropdownDefaultsVisible(true);
                                setIsProcessed(false);
                                setProcessedImage(null);
                                setOverlayImage(null);
                                setIsProcessing(false);
                                setProcessedLoaded(false);
                                setOverlayLoaded(false);
                              }
                        }
                        style={{
                          width: '3vh',
                          height: '3vh',
                          borderRadius: '50%',
                          background: 'rgba(255,255,255,0.18)',
                          border: '1px solid rgba(255,255,255,0.3)',
                          color: 'white',
                          fontSize: '2vh',
                          lineHeight: 0,
                          textAlign: 'center',
                          cursor: uiDisabled ? 'not-allowed' : 'pointer',
                          opacity: uiDisabled ? 0.6 : 1,
                        }}
                      >
                        –
                      </button>

                      {/* numeric input */}
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={String(pendingNumColors)}
                        disabled={uiDisabled}
                        onChange={
                          uiDisabled
                            ? undefined
                            : (e) => {
                                let raw = e.target.value.replace(/\D/g, '');
                                if (raw === '') raw = '2';

                                let value = parseInt(raw, 10);
                                if (!Number.isFinite(value)) value = 2;
                                value = Math.max(2, Math.min(8, value));

                                setPendingNumColors(value);
                                setProcessDisabled(false);
                                setDropdownDefaultsVisible(true);
                                setIsProcessed(false);
                                setProcessedImage(null);
                                setOverlayImage(null);
                                setIsProcessing(false);
                                setProcessedLoaded(false);
                                setOverlayLoaded(false);
                              }
                        }
                        style={{
                          width: '3vh',
                          fontSize: '2vh',
                          padding: '0.4vh 0.6vh',
                          background: 'rgba(255,255,255,0.12)',
                          border: '1px solid rgba(255,255,255,0.25)',
                          borderRadius: '0.6vh',
                          color: 'white',
                          textAlign: 'center',
                          outline: 'none',
                          backdropFilter: 'blur(4px)',
                          margin: 0,
                          opacity: uiDisabled ? 0.6 : 1,
                          cursor: uiDisabled ? 'not-allowed' : 'text',
                        }}
                      />

                      {/* + button */}
                      <button
                        type="button"
                        disabled={uiDisabled}
                        onClick={
                          uiDisabled
                            ? undefined
                            : () => {
                                let value = Math.min(8, pendingNumColors + 1);
                                setPendingNumColors(value);
                                setProcessDisabled(false);
                                setDropdownDefaultsVisible(true);
                                setIsProcessed(false);
                                setProcessedImage(null);
                                setOverlayImage(null);
                                setIsProcessing(false);
                                setProcessedLoaded(false);
                                setOverlayLoaded(false);
                              }
                        }
                        style={{
                          width: '3vh',
                          height: '3vh',
                          borderRadius: '50%',
                          background: 'rgba(255,255,255,0.18)',
                          border: '1px solid rgba(255,255,255,0.3)',
                          color: 'white',
                          fontSize: '2vh',
                          lineHeight: 0,
                          textAlign: 'center',
                          cursor: uiDisabled ? 'not-allowed' : 'pointer',
                          opacity: uiDisabled ? 0.6 : 1,
                        }}
                      >
                        +
                      </button>
                    </div>
                  </div>

                  {/* Flags */}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.4vh',
                      fontSize: '2vh',
                      opacity: uiDisabled ? 0.6 : 1,
                      pointerEvents: uiDisabled ? 'none' : 'auto',
                    }}
                  >
                    {/* Remove Noisy Background */}
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: uiDisabled ? 'not-allowed' : 'pointer',
                        gap: '1vh',
                      }}
                    >
                      <span>Remove Noisy Background</span>
                      <span
                        style={{
                          position: 'relative',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={removeNoisyBackground}
                          disabled={uiDisabled}
                          onChange={
                            uiDisabled
                              ? undefined
                              : (e) => {
                                  setRemoveNoisyBackground(e.target.checked);
                                  setDropdownDefaultsVisible(true);
                                  setProcessedImage(null);
                                  setOverlayImage(null);
                                  setProcessDisabled(false);
                                  setIsProcessed(false);
                                  setIsProcessing(false);
                                  setProcessedLoaded(false);
                                  setOverlayLoaded(false);
                                }
                          }
                          style={{
                            appearance: 'none',
                            WebkitAppearance: 'none',
                            MozAppearance: 'none',
                            width: '2.4vh',
                            height: '2.4vh',
                            borderRadius: '0.5vh',
                            border: '0.25vh solid rgba(255,255,255,0.8)',
                            backgroundColor: 'rgba(15,23,42,0.6)',
                            display: 'inline-block',
                            cursor: uiDisabled ? 'not-allowed' : 'pointer',
                          }}
                        />
                        {removeNoisyBackground && (
                          <span
                            style={{
                              position: 'absolute',
                              fontSize: '1.8vh',
                              color: 'white',
                              pointerEvents: 'none',
                            }}
                          >
                            ✓
                          </span>
                        )}
                      </span>
                    </label>

                    {/* Close Background Holes */}
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: uiDisabled ? 'not-allowed' : 'pointer',
                        gap: '1vh',
                      }}
                    >
                      <span>Close Background Holes</span>
                      <span
                        style={{
                          position: 'relative',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={closeBackgroundHoles}
                          disabled={uiDisabled}
                          onChange={
                            uiDisabled
                              ? undefined
                              : (e) => {
                                  setCloseBackgroundHoles(e.target.checked);
                                  setDropdownDefaultsVisible(true);
                                  setProcessedImage(null);
                                  setOverlayImage(null);
                                  setProcessDisabled(false);
                                  setIsProcessed(false);
                                  setIsProcessing(false);
                                  setProcessedLoaded(false);
                                  setOverlayLoaded(false);
                                }
                          }
                          style={{
                            appearance: 'none',
                            WebkitAppearance: 'none',
                            MozAppearance: 'none',
                            width: '2.4vh',
                            height: '2.4vh',
                            borderRadius: '0.5vh',
                            border: '0.25vh solid rgba(255,255,255,0.8)',
                            backgroundColor: 'rgba(15,23,42,0.6)',
                            display: 'inline-block',
                            cursor: uiDisabled ? 'not-allowed' : 'pointer',
                          }}
                        />
                        {closeBackgroundHoles && (
                          <span
                            style={{
                              position: 'absolute',
                              fontSize: '1.8vh',
                              color: 'white',
                              pointerEvents: 'none',
                            }}
                          >
                            ✓
                          </span>
                        )}
                      </span>
                    </label>

                    {/* Remove Small Islands */}
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: uiDisabled ? 'not-allowed' : 'pointer',
                        gap: '1vh',
                      }}
                    >
                      <span>Remove Small Islands</span>
                      <span
                        style={{
                          position: 'relative',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={removeSmallIslands}
                          disabled={uiDisabled}
                          onChange={
                            uiDisabled
                              ? undefined
                              : (e) => {
                                  setRemoveSmallIslands(e.target.checked);
                                  setDropdownDefaultsVisible(true);
                                  setProcessedImage(null);
                                  setOverlayImage(null);
                                  setProcessDisabled(false);
                                  setIsProcessed(false);
                                  setIsProcessing(false);
                                  setProcessedLoaded(false);
                                  setOverlayLoaded(false);
                                }
                          }
                          style={{
                            appearance: 'none',
                            WebkitAppearance: 'none',
                            MozAppearance: 'none',
                            width: '2.4vh',
                            height: '2.4vh',
                            borderRadius: '0.5vh',
                            border: '0.25vh solid rgba(255,255,255,0.8)',
                            backgroundColor: 'rgba(15,23,42,0.6)',
                            display: 'inline-block',
                            cursor: uiDisabled ? 'not-allowed' : 'pointer',
                          }}
                        />
                        {removeSmallIslands && (
                          <span
                            style={{
                              position: 'absolute',
                              fontSize: '1.8vh',
                              color: 'white',
                              pointerEvents: 'none',
                            }}
                          >
                            ✓
                          </span>
                        )}
                      </span>
                    </label>

                    {/* Speckle Auto-Cleanup */}
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: uiDisabled ? 'not-allowed' : 'pointer',
                        gap: '1vh',
                      }}
                    >
                      <span>Speckle Auto-Cleanup</span>
                      <span
                        style={{
                          position: 'relative',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={speckleAutoCleanup}
                          disabled={uiDisabled}
                          onChange={
                            uiDisabled
                              ? undefined
                              : (e) => {
                                  setSpeckleAutoCleanup(e.target.checked);
                                  setDropdownDefaultsVisible(true);
                                  setProcessedImage(null);
                                  setOverlayImage(null);
                                  setProcessDisabled(false);
                                  setIsProcessed(false);
                                  setIsProcessing(false);
                                  setProcessedLoaded(false);
                                  setOverlayLoaded(false);
                                }
                          }
                          style={{
                            appearance: 'none',
                            WebkitAppearance: 'none',
                            MozAppearance: 'none',
                            width: '2.4vh',
                            height: '2.4vh',
                            borderRadius: '0.5vh',
                            border: '0.25vh solid rgba(255,255,255,0.8)',
                            backgroundColor: 'rgba(15,23,42,0.6)',
                            display: 'inline-block',
                            cursor: uiDisabled ? 'not-allowed' : 'pointer',
                          }}
                        />
                        {speckleAutoCleanup && (
                          <span
                            style={{
                              position: 'absolute',
                              fontSize: '1.8vh',
                              color: 'white',
                              pointerEvents: 'none',
                            }}
                          >
                            ✓
                          </span>
                        )}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {inputImage ? (
              <>
                <img
                  src={inputImage}
                  alt="Input"
                  style={imgStyle}
                  onLoad={() => {
                    orangeBoxRef.current?.scrollTo({
                      top: 0,
                      behavior: 'smooth',
                    });
                  }}
                />
                {isProcessing && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      backgroundColor: 'rgba(255,255,255,0.7)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 3,
                      pointerEvents: 'none',
                    }}
                  >
                    <img
                      src="/rendering.gif"
                      alt="Rendering"
                      style={{
                        width: 200,
                        height: 200,
                        objectFit: 'contain',
                      }}
                    />
                  </div>
                )}
              </>
            ) : (
              <span
                style={{
                  fontSize: '2.8vh',
                  color: '#64748b',
                  textAlign: 'center',
                  padding: '1vw',
                }}
              >
                Click Here to upload image
              </span>
            )}
          </div>
        </div>

        {/* Processed + Overlay */}
        {(processedImage || overlayImage) && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: '100%',
            }}
          >
            <div
              ref={kcContainerRef}
              style={{
                ...boxStyle(),
                position: 'relative',
              }}
              onMouseEnter={uiDisabled ? undefined : () => setKcHover(true)}
              onMouseLeave={uiDisabled ? undefined : () => setKcHover(false)}
            >
              {/* Sliding Advanced Controls overlay */}
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: 0,
                  zIndex: 30,
                  background:
                    'linear-gradient(to bottom, rgba(15,23,42,0.96), rgba(15,23,42,0.6), transparent)',
                  color: 'white',
                  transform: showAdvancedOverlayPanel
                    ? 'translateY(0%)'
                    : 'translateY(calc(-100% + 2.8vh))',
                  transition: 'transform 0.25s ease-in-out',
                  willChange: 'transform',
                  fontSize: '0.8rem',
                  pointerEvents: uiDisabled ? 'none' : 'auto',
                  opacity: uiDisabled ? 0.6 : 1,
                }}
              >
                {/* Handle */}
                <div
                  onClick={
                    uiDisabled
                      ? undefined
                      : (e) => {
                          e.stopPropagation();
                          setShowAdvancedOverlayPanel((prev) => {
                            if (!prev) capitanApi?.onOpenAdvanced?.();
                            return !prev;
                          });
                          if (!showColorControls) {
                            setShowColorControls(true);
                          }
                        }
                  }
                  style={{
                    height: '2.8vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: uiDisabled ? 'not-allowed' : 'pointer',
                    borderBottom: '1px solid rgba(255,255,255,0.4)',
                    backgroundColor: 'rgba(112,112,112,0.4)',
                  }}
                >
                  <span
                    style={{
                      fontSize: '2.5vh',
                      fontWeight: 600,
                      letterSpacing: '0.03em',
                    }}
                  >
                    {showAdvancedOverlayPanel
                      ? '▲ Advanced Color Controls ▲'
                      : '▼ Advanced Color Controls ▼'}
                  </span>
                </div>

                {/* CONTENT: Advanced Color Controls */}
                <div
                  style={{
                    padding: showAdvancedOverlayPanel
                      ? '8px 10px 10px'
                      : '0 10px 0',
                    maxHeight: showAdvancedOverlayPanel ? '55%' : 0,
                    overflowY: 'auto',
                    overflow: 'hidden',
                    backgroundColor: 'rgba(112,112,112,0.5)',
                    fontSize: '2vh',
                    opacity: showAdvancedOverlayPanel ? 1 : 0,
                    transition:
                      'max-height 0.25s ease, opacity 0.25s ease, padding 0.25s ease',
                  }}
                >
                  <style>
                    {`
                      .adv-select {
                        background: rgba(255,255,255,0.12) !important;
                        border: 1px solid rgba(255,255,255,0.25) !important;
                        color: #e5e7eb !important;
                        backdrop-filter: blur(4px);
                      }

                      .adv-select:focus {
                        outline: none;
                        border-color: rgba(148,163,184,0.9) !important;
                      }

                      .adv-select option {
                        background-color: rgba(15,23,42,0.6) !important;
                        color: #e5e7eb !important;
                      }

                      .adv-select option:checked,
                      .adv-select option:focus {
                        background-color: rgba(55,65,81,0.6) !important;
                        color: #e5e7eb !important;
                      }
                    `}
                  </style>

                  {/* ✅ disable all advanced controls while processing */}
                  <div
                    style={{
                      pointerEvents: uiDisabled ? 'none' : 'auto',
                      opacity: uiDisabled ? 0.6 : 1,
                    }}
                  >
                    {renderAdvancedControls()}
                  </div>
                </div>
              </div>

              {processedImage && (
                <img
                  src={processedImage}
                  alt="Processed"
                  style={layerStyle(1)}
                  onLoad={(e) => {
                    setBaseNaturalSize({
                      w: e.target.naturalWidth || 0,
                      h: e.target.naturalHeight || 0,
                    });

                    setProcessedLoaded(true);
                    setTimeout(() => {
                      orangeBoxRef.current?.scrollTo({
                        top: orangeBoxRef.current.scrollHeight,
                        behavior: 'smooth',
                      });
                    }, 100);
                  }}
                />
              )}

              {overlayImage && (
                <img
                  src={overlayImage}
                  alt="Overlay"
                  style={layerStyle(2)}
                  onLoad={() => {
                    setOverlayLoaded(true);
                  }}
                />
              )}

              {/* KEYCHAIN OVERLAY CANVAS – disable interactions while processing */}
              {(processedImage || overlayImage) && (
                <>
                  {/* KC overlay image in bottom-left – click to enable loop */}
                  {showKCOverlay && (
                    <img
                      src="/KCoverlay.svg"
                      alt="KC Overlay"
                      onClick={uiDisabled ? undefined : handleKCOverlayClick}
                      style={{
                        position: 'absolute',
                        bottom: '1vh',
                        left: '1vh',
                        width: '14vmin',
                        height: 'auto',
                        zIndex: 16,
                        opacity: 0.88,
                        pointerEvents: uiDisabled ? 'none' : 'auto',
                        cursor: uiDisabled ? 'not-allowed' : 'pointer',
                      }}
                    />
                  )}

                  {/* Keychain canvas – only visible after KC overlay was clicked */}
                  {kcEnabled && (
                    <canvas
                      ref={kcCanvasRef}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        zIndex: 15,
                        cursor: uiDisabled ? 'not-allowed' : 'crosshair',
                        pointerEvents: uiDisabled ? 'none' : 'auto',
                        opacity: uiDisabled ? 0.6 : 1,
                      }}
                      onMouseMove={uiDisabled ? undefined : handleKcPointerMove}
                      onClick={uiDisabled ? undefined : handleKcCanvasClick}
                    />
                  )}
                </>
              )}

              {isProcessing && (
                <img
                  src="/rendering.gif"
                  alt="Rendering Animation"
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 200,
                    height: 200,
                    objectFit: 'contain',
                    zIndex: 20,
                    pointerEvents: 'none',
                  }}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>

    {/* Unified Process / Progress button */}
    <div
      style={{
        marginTop: '0vh',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <button
        onClick={
          !uiDisabled && credits > 0 && !processDisabled && filename
            ? () => {
                capitanApi?.onProcessStart?.();
                handleProcessClick(false);
              }
            : null
        }
        disabled={uiDisabled || credits === 0 || processDisabled || !filename}
        style={{
          width: '80vmin',
          padding: '0.8vmin 0',
          background: errorState ? '#fecaca' : '#93c5fd',
          border: '0px solid #94a3b8',
          borderRadius: '10px',
          fontSize: '2.8vh',
          fontWeight: 'bold',
          textAlign: 'center',
          cursor:
            !uiDisabled && credits > 0 && !processDisabled && filename
              ? 'pointer'
              : 'not-allowed',
          position: 'relative',
          overflow: 'hidden',
          opacity: uiDisabled ? 0.75 : 1,
        }}
      >
        {isProcessing && progress !== null && (
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              height: '100%',
              width: `${progress}%`,
              backgroundColor: errorState ? '#f87171' : '#0ea5e9',
              transition: 'width 0.4s ease',
              zIndex: 0,
            }}
          ></div>
        )}

        <span style={{ position: 'relative', zIndex: 2 }}>
          {errorState
            ? 'ERROR'
            : isProcessing
            ? `Processing… ${progress ?? 0}%`
            : 'Process Image'}
        </span>
      </button>
    </div>
  </div>
);
 }

const boxStyle = () => ({
  width: '100%',
  height: '100%',
  border: '2px dashed #94a3b8',
  borderRadius: '10px',
  backgroundColor: '#f8fafc',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'hidden',
});

const imgStyle = {
  width: '100%',
  height: '100%',
  objectFit: 'contain',
};

const layerStyle = (z) => ({
  position: 'absolute',
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  top: 0,
  left: 0,
  zIndex: z,
  pointerEvents: 'none',
});
